import {Component, computed, inject, OnDestroy, signal} from '@angular/core';
import {FormsModule} from '@angular/forms';
import {Router} from '@angular/router';
import {firstValueFrom} from 'rxjs';
import {AuthService, DotLoadingServices} from '../../../../../../core';
import {NotificationService} from "../../../../../services/notification.service";
import {CustomerManagementService} from '../../services/customers.service';
import {CustomerAddressDTO, CustomerContactPersonDTO, CustomerCreateDTO} from '../../interfaces/customer.entity';
import {SearchSalesExecutive} from "../../../../../shared/common/search-sales-owner/search-sales-executive.component";
import {
    SearchCountryComponent,
    SearchCurrencyComponent,
    SearchInvoiceTypeComponent,
    SearchPersonTitleComponent
} from '../../../../../shared';
import {PhoneNumberOnlyDirective} from '../../../Shared/Directives/phone-numbers-only.directive'
import {CustomerModalStateService} from '../../services/customer-modal-state.service';
import {
    SearchPaymentTermsComponent
} from 'src/app/components/shared/common/search-payment-terms/search-payment-terms.component';
import {PositiveNumberOnlyDirective} from '../../../Shared/Directives/Positive-number-only.directive';

/** One user-facing validation problem, listed in the order the fields appear on the form. */
interface CustomerFieldError {
    /** Matches the `field` the template asks about, and the wrapper element id to focus. */
    field: string;
    /** Human name, used when several fields are listed in one message. */
    label: string;
    /** Shown under the field itself. */
    message: string;
}

@Component({
  selector: 'app-customer-create',
    imports: [
        FormsModule,
        SearchSalesExecutive,
        SearchCurrencyComponent,
        SearchInvoiceTypeComponent,
        SearchPersonTitleComponent,
        SearchCountryComponent,
        PhoneNumberOnlyDirective,
        SearchPaymentTermsComponent,
        PositiveNumberOnlyDirective,
    ],
  templateUrl: './customer-create.component.html',
  styleUrl: './customer-create.component.scss',
  standalone: true
})
export class CustomerCreateComponent implements OnDestroy {

  resetSearchTrigger = signal<boolean>(false);

  /** Set once Create/Update is pressed, so required-field errors only appear after an attempt. */
  protected submitted = signal<boolean>(false);

  protected isVatRequired = computed(() => {
    const invoiceTypeId = this.customer().invoiceTypeId;
    return invoiceTypeId === 1 || invoiceTypeId === 2;
  });
  
  protected customerService = inject(CustomerManagementService);
  protected authService = inject(AuthService);
  protected notification = inject(NotificationService);
  protected loading = inject(DotLoadingServices);
  protected router = inject(Router);
  protected modalState = inject(CustomerModalStateService);

  protected isValidatingCode = signal<boolean>(false);
  protected originalCustomerCode = signal<string>('');
  protected isUpdateCustomer = signal<boolean>(false);
  protected currentCustomerId = signal<number | null>(null);
  
  // Store original customer data for comparison
  private originalCustomer: CustomerCreateDTO | null = null;

  protected customer = signal<CustomerCreateDTO>({
    customerName: '',
    customerVatRegNo: '',
    customerSvatRegNo: '',
    paymentTerm: '',
    creditPeriod: undefined,
    currencyId: -1,
    isTaxApply: false,
    taxGroupId: null,
    invoiceTypeId: -1,
    telephoneNo: '',
    faxNo: '',
    emailAddress: '',
    invFormat: '',
    plFormat: '',
    boxFormat: '',
    pckFormat: '',
    isActive: 0,
    salesOwnerId: -1,
    wearhouseCode: '',
    isBOIRegistered: false,
    addresses: [],
    contactPersons: []
  });

  constructor() {
    this.initializeActiveCustomer();
  }

  private initializeActiveCustomer(): void {
    const activeCustomer = this.customerService.active();
    if (activeCustomer) {
      this.isUpdateCustomer.set(true);
      this.currentCustomerId.set(activeCustomer.customerId);
      this.populateDataForUpdate(activeCustomer);
    }
  }

  get isCustomerCreate(): boolean {
    return !this.customerService.active();
  }

  protected onFieldSelected(field: keyof CustomerCreateDTO, value: any): void {
    console.log(field);
    
    this.customer.update(current => ({ ...current, [field]: value }));
  }

  protected onCountryFieldSelected(
    index: number,
    field: keyof CustomerAddressDTO,
    value: any
  ): void {
    this.customer.update(current => {
      const updatedAddresses = [...current.addresses];
      updatedAddresses[index] = {
        ...updatedAddresses[index],
        [field]: value
      };
      return {
        ...current,
        addresses: updatedAddresses
      };
    });
  }

  protected updateField(field: keyof CustomerCreateDTO, value: any): void {
    this.customer.update(current => ({ ...current, [field]: value }));
  }

  protected onTaxApplyChange(value: boolean): void {
    this.customer.update(current => ({
      ...current,
      isTaxApply: value,
      taxGroupId: value ? current.taxGroupId : -1
    }));
  }

  // Address Management
  protected addAddress(): void {
    const newAddress: CustomerAddressDTO = {
      addressLineOne: '',
      addressLineTwo: '',
      addressLineThree: '',
      country: -1,
      show: '',
      useForInvoice: false,
      isDefaultInvoice: false,
      useForDelivery: false,
      isDefaultDelivery: false,
      status:false
    };
    this.customer.update(current => ({
      ...current,
      addresses: [...current.addresses, newAddress]
    }));
  }

  protected removeAddress(index: number): void {
    this.customer.update(current => ({
      ...current,
      addresses: current.addresses.filter((_, i) => i !== index)
    }));
  }

  protected updateAddressField(index: number, field: keyof CustomerAddressDTO, value: any): void {
    this.customer.update(current => {
      const updatedAddresses = [...current.addresses];
      updatedAddresses[index] = { ...updatedAddresses[index], [field]: value };
      return { ...current, addresses: updatedAddresses };
    });
  }

  protected onAddressFieldSelected(index: number, field: keyof CustomerAddressDTO, value: any): void {
    this.updateAddressField(index, field, value);
  }

  protected onInvoicingDefaultChange(index: number, value: boolean): void {
    if (value) {
      this.customer.update(current => {
        const updatedAddresses = current.addresses.map((addr, i) => ({
          ...addr,
          isDefaultInvoice: i === index
        }));
        return { ...current, addresses: updatedAddresses };
      });
    } else {
      this.updateAddressField(index, 'isDefaultInvoice', false);
    }
  }

  protected onDeliveryDefaultChange(index: number, value: boolean): void {
    if (value) {
      this.customer.update(current => {
        const updatedAddresses = current.addresses.map((addr, i) => ({
          ...addr,
          isDefaultDelivery: i === index
        }));
        return { ...current, addresses: updatedAddresses };
      });
    } else {
      this.updateAddressField(index, 'isDefaultDelivery', false);
    }
  }

  // Contact Person Management
  protected addContactPerson(): void {
    const newContact: CustomerContactPersonDTO = {
      title: 0,
      contactPersonName: '',
      contactPersonDesignation: '',
      contactPersonOfficePhoneNumber: '',
      contactPersonMobileNumber: '',
      contactPersonEmail: '',
      status:true
    };
    this.customer.update(current => ({
      ...current,
      contactPersons: [...current.contactPersons, newContact]
    }));
  }

  protected removeContactPerson(index: number): void {
    this.customer.update(current => ({
      ...current,
      contactPersons: current.contactPersons.filter((_, i) => i !== index)
    }));
  }

  protected updateContactField(index: number, field: keyof CustomerContactPersonDTO, value: any): void {
    this.customer.update(current => {
      const updatedContacts = [...current.contactPersons];
      updatedContacts[index] = { ...updatedContacts[index], [field]: value };
      return { ...current, contactPersons: updatedContacts };
    });
  }

  protected onContactFieldSelected(index: number, field: keyof CustomerContactPersonDTO, value: any): void {
    this.updateContactField(index, field, value);
  }

  // ===== NEW METHODS FOR TOGGLE VALIDATION =====
  
  // Check if any field has been edited
  protected isFieldsEdited(): boolean {
    if (!this.originalCustomer || !this.isUpdateCustomer()) {
      return false;
    }
    
    return JSON.stringify(this.customer()) !== JSON.stringify(this.originalCustomer);
  }

  // Check if required fields are missing
  protected hasRequiredFieldsMissing(): boolean {
    const c = this.customer();
    
    // Check basic required fields
    const basicFieldsMissing = !c.customerName?.trim() || 
                               !c.paymentTerm?.trim();
    
    // Check VAT required fields if tax is applied
    const vatFieldsMissing = this.isVatRequired() && 
                            (!c.customerVatRegNo?.trim() || !c.customerSvatRegNo?.trim());
    
    // Check credit period if payment term is credit
    const creditPeriodMissing = c.paymentTerm === 'cr' && (!c.creditPeriod || c.creditPeriod <= 0);
    
    // Check required IDs
    const requiredIdsInvalid = [c.currencyId, c.invoiceTypeId, c.salesOwnerId].includes(-1);
    
    return basicFieldsMissing || vatFieldsMissing || creditPeriodMissing || requiredIdsInvalid;
  }

  // Check if toggle should be disabled
  protected isToggleDisabled(): boolean {
    return this.isFieldsEdited() || this.hasRequiredFieldsMissing();
  }

  // Get tooltip message for disabled toggle
  protected getToggleTooltipMessage(): string {
    if (this.hasRequiredFieldsMissing()) {
      return 'Please fill all required fields before activating/deactivating';
    }
    if (this.isFieldsEdited()) {
      return 'Please update customer before activating or deactivating';
    }
    return '';
  }

  // Store original customer data
  private storeOriginalCustomer(): void {
    this.originalCustomer = JSON.parse(JSON.stringify(this.customer()));
  }

  // Reset original customer data after successful update
  private resetOriginalCustomer(): void {
    this.originalCustomer = JSON.parse(JSON.stringify(this.customer()));
  }

  // ===== END NEW METHODS =====

  /**
   * The required-field problems, itemised so each one can be shown against its own
   * field. These are exactly the conditions isValidCreation() has always used — only
   * the reporting is new, nothing became stricter or looser.
   */
  protected validationErrors = computed<CustomerFieldError[]>(() => {
    const c = this.customer();
    const errors: CustomerFieldError[] = [];

    if (!c.customerName?.trim()) {
      errors.push({ field: 'customerName', label: 'Customer Name', message: 'Customer Name is required.' });
    }
    if (c.invoiceTypeId === -1) {
      errors.push({ field: 'invoiceTypeId', label: 'Invoice Type', message: 'Pick an invoice type from the dropdown list.' });
    }
    if (this.isVatRequired() && !c.customerVatRegNo?.trim()) {
      errors.push({ field: 'customerVatRegNo', label: 'VAT Reg No', message: 'VAT Reg No is required for this invoice type.' });
    }
    if (this.isVatRequired() && !c.customerSvatRegNo?.trim()) {
      errors.push({ field: 'customerSvatRegNo', label: 'SVAT Reg No', message: 'SVAT Reg No is required for this invoice type.' });
    }
    if (!c.paymentTerm || c.paymentTerm.trim() === '') {
      errors.push({ field: 'paymentTerm', label: 'Payment Term', message: 'Pick a payment term from the dropdown list.' });
    }
    if (c.paymentTerm === 'cr' && (!c.creditPeriod || c.creditPeriod <= 0)) {
      errors.push({ field: 'creditPeriod', label: 'Credit Period', message: 'Credit Period must be greater than 0 for credit terms.' });
    }
    if (c.currencyId === -1) {
      errors.push({ field: 'currencyId', label: 'Currency', message: 'Pick a currency from the dropdown list.' });
    }
    if (c.salesOwnerId === -1) {
      errors.push({ field: 'salesOwnerId', label: 'Sales Executive', message: 'Pick a sales executive from the dropdown list.' });
    }

    // Addresses and contact persons are only required when creating.
    if (!this.isUpdateCustomer()) {
      if (c.addresses.length === 0) {
        errors.push({ field: 'addresses', label: 'Addresses', message: 'Add at least one address.' });
      }
      c.addresses.forEach((addr, i) => {
        const at = ` (Address ${i + 1})`;
        if (!addr.addressLineOne?.trim()) {
          errors.push({ field: `address-${i}-addressLineOne`, label: `Address Line One${at}`, message: 'Address Line One is required.' });
        }
        if (!addr.addressLineTwo?.trim()) {
          errors.push({ field: `address-${i}-addressLineTwo`, label: `Address Line Two${at}`, message: 'Address Line Two is required.' });
        }
        if (addr.country === -1) {
          errors.push({
            field: `address-${i}-country`, label: `Country${at}`,
            // The picker only commits a country when an option is clicked, so typed
            // text can look complete while nothing is actually selected.
            message: 'Pick a country from the dropdown list — typing the name on its own does not select it.'
          });
        }
        if (!addr.show?.trim()) {
          errors.push({ field: `address-${i}-show`, label: `Show As${at}`, message: 'Show As is required.' });
        }
      });

      if (c.contactPersons.length === 0) {
        errors.push({ field: 'contactPersons', label: 'Contact Persons', message: 'Add at least one contact person.' });
      }
      c.contactPersons.forEach((contact, i) => {
        const at = ` (Contact Person ${i + 1})`;
        if (contact.title === -1) {
          errors.push({ field: `contact-${i}-title`, label: `Title${at}`, message: 'Pick a title from the dropdown list.' });
        }
        if (!contact.contactPersonName?.trim()) {
          errors.push({ field: `contact-${i}-contactPersonName`, label: `Name${at}`, message: 'Contact Person Name is required.' });
        }
      });
    }

    return errors;
  });

  /**
   * E-mail format problems. Kept apart from validationErrors because these have never
   * blocked the Create/Update button — they are checked at submit time, and are now
   * also shown inline as soon as the value is wrong.
   */
  protected formatErrors = computed<CustomerFieldError[]>(() => {
    const c = this.customer();
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    const errors: CustomerFieldError[] = [];

    if (c.emailAddress && !emailRegex.test(c.emailAddress)) {
      errors.push({ field: 'emailAddress', label: 'E-mail Address', message: 'Enter a valid e-mail address, e.g. name@company.com.' });
    }

    c.contactPersons.forEach((contact, i) => {
      if (contact.contactPersonEmail && !emailRegex.test(contact.contactPersonEmail)) {
        errors.push({
          field: `contact-${i}-contactPersonEmail`,
          label: `E-mail (Contact Person ${i + 1})`,
          message: 'Enter a valid e-mail address, e.g. name@company.com.'
        });
      }
    });

    return errors;
  });

  /** Message to show under one field: format problems always, required ones after a submit attempt. */
  protected errorFor(field: string): string | null {
    const format = this.formatErrors().find(e => e.field === field);
    if (format) return format.message;
    if (!this.submitted()) return null;
    return this.validationErrors().find(e => e.field === field)?.message ?? null;
  }

  /** Every error currently worth showing in the summary at the top of the form. */
  protected visibleErrors = computed<CustomerFieldError[]>(() =>
    this.submitted()
      ? [...this.validationErrors(), ...this.formatErrors()]
      : this.formatErrors()
  );

  /** Scrolls to one field and focuses its input — used by the summary links. */
  protected goToField(field: string): void {
    const host = document.getElementById(`field-${field}`);
    if (!host) return;
    host.scrollIntoView({ behavior: 'smooth', block: 'center' });
    host.querySelector('input')?.focus();
  }

  /** Sends the user to the first problem after a failed submit. */
  private focusFirstError(): void {
    const first = this.visibleErrors()[0];
    if (first) this.goToField(first.field);
  }

  protected isValidCreation(): boolean {
    // Unchanged: the code check blocks submission while it is still running.
    if (this.isValidatingCode()) {
      return true;
    }

    return this.validationErrors().length > 0;
  }

  protected async createCustomer(): Promise<void> {
    this.submitted.set(true);

    if (this.isValidatingCode()) {
      this.notification.set({
        type: 'alert',
        message: 'Please wait while the customer code is being validated.',
        timeout: 3000
      });
      return;
    }

    // Required fields — the same rules as before, but the message now names them.
    if (this.isValidCreation()) {
      const errors = this.validationErrors();
      this.notification.set({
        type: 'alert',
        message: errors.length === 1
          ? errors[0].message
          : `${errors.length} fields still need attention: ${errors.map(e => e.label).join(', ')}.`,
        timeout: 5000
      });
      this.focusFirstError();
      return;
    }

    // E-mail format — same checks as before, but pointing at the field that is wrong.
    const formatProblem = this.formatErrors()[0];
    if (formatProblem) {
      this.notification.set({
        type: 'alert',
        message: `${formatProblem.label}: ${formatProblem.message}`,
        timeout: 5000
      });
      this.focusFirstError();
      return;
    }

    try {
      this.loading.set(true);
      const customerData = { ...this.customer() };
      const isUpdate = this.isUpdateCustomer();

      if (isUpdate) {
        await this.updateCustomerGeneralDetails();
      } else {
        await this.createNewCustomer();
      }

    } catch (error) {
            const isUpdate = this.isUpdateCustomer();
      this.notification.set({
        type: 'error',
        message: `Failed to ${isUpdate ? 'update' : 'create'} the customer.`,
      });
      console.error(error);
    } finally {
      this.loading.set(false);
    }
  }

  private async createNewCustomer(): Promise<void> {
    const customerData = { ...this.customer() };

    const formattedAddresses = customerData.addresses.map(address => ({
      ...address,
      useForInvoice: address.useForInvoice ? 1 : 0,
      isDefaultInvoice: address.isDefaultInvoice ? 1 : 0,
      useForDelivery: address.useForDelivery ? 1 : 0,
      isDefaultDelivery: address.isDefaultDelivery ? 1 : 0
    }));

    const formattedCustomerData = {
      ...customerData,
      isTaxApply: customerData.isTaxApply ? 1 : 0,
      isBOIRegistered: customerData.isBOIRegistered ? 1 : 0,
      isActive: customerData.isActive ? 1 : 0,
      addresses: formattedAddresses,
      createdBy: this.authService.username()
    };

    await firstValueFrom(this.customerService.create(formattedCustomerData));

    this.customerService.notifyCustomerCreated();
    this.notification.set({
      type: 'success',
      message: 'Customer created successfully.',
    });

    this.router.navigate(['/customer/customer-grid']).then(() => {
      this.cancel();
    });
  }

protected navigateToAddressManagement(): void {
  this.modalState.updateModal.set(true);
    this.modalState.generalUpdateModal.set(false);
    this.modalState.contactPersonspdateModal.set(false);
    this.modalState.addressesUpdateModal.set(true);
}

protected navigateToContactManagement(): void {
    this.modalState.updateModal.set(true);
    this.modalState.generalUpdateModal.set(false);
    this.modalState.contactPersonspdateModal.set(true);
    this.modalState.addressesUpdateModal.set(false);
}
  private async updateCustomerGeneralDetails(): Promise<void> {
    const customerData = { ...this.customer() };
    const customerId = this.currentCustomerId();

    if (!customerId) {
      throw new Error('Customer ID not found');
    }

    // Prepare general details payload (without addresses and contact persons)
    const generalDetailsPayload = {
      customerId: customerId,
      customerName: customerData.customerName,
      customerVatRegNo: customerData.customerVatRegNo,
      customerSvatRegNo: customerData.customerSvatRegNo,
      paymentTerm: customerData.paymentTerm,
      creditPeriod: customerData.creditPeriod,
      currencyId: customerData.currencyId,
      isTaxApply: customerData.isTaxApply ? 1 : 0,
      taxGroupId: customerData.taxGroupId,
      invoiceTypeId: customerData.invoiceTypeId,
      telephoneNo: customerData.telephoneNo,
      faxNo: customerData.faxNo,
      emailAddress: customerData.emailAddress,
      invFormat: customerData.invFormat,
      plFormat: customerData.plFormat,
      boxFormat: customerData.boxFormat,
      pckFormat: customerData.pckFormat,
      isActive: customerData.isActive ? 1 : 0,
      salesOwnerId: customerData.salesOwnerId,
      wearhouseCode: customerData.wearhouseCode,
      isBOIRegistered: customerData.isBOIRegistered ? 1 : 0,
      updatedBy: this.authService.username()
    };

    // Update general details
    await firstValueFrom(this.customerService.update(generalDetailsPayload));

    // Reset original customer data after successful update
    this.resetOriginalCustomer();

    this.customerService.notifyCustomerCreated();
    this.notification.set({
      type: 'success',
      message: 'Customer updated successfully.',
    });
  }

  // Separate method to update addresses
  protected async updateAddresses(): Promise<void> {
    const customerId = this.currentCustomerId();
    
    if (!customerId) {
      this.notification.set({
        type: 'alert',
        message: 'Customer ID not found. Cannot update addresses.',
        timeout: 3000
      });
      return;
    }

    const customerData = { ...this.customer() };

    // Validate addresses
    const hasValidAddress = customerData.addresses.length > 0 && customerData.addresses.every(addr => {
      return addr.addressLineOne?.trim() && 
             addr.addressLineTwo?.trim() && 
             addr.show?.trim() && 
             addr.country !== -1;
    });

    if (!hasValidAddress) {
      this.notification.set({
        type: 'alert',
        message: 'Please fill all required address fields.',
        timeout: 3000
      });
      return;
    }

    try {
      this.loading.set(true);

      const formattedAddresses = customerData.addresses.map(address => ({
        addressLineOne: address.addressLineOne,
        addressLineTwo: address.addressLineTwo,
        addressLineThree: address.addressLineThree || '',
        show: address.show,
        country: address.country,
        useForInvoice: address.useForInvoice ? 1 : 0,
        useForDelivery: address.useForDelivery ? 1 : 0,
        isDefaultInvoice: address.isDefaultInvoice ? 1 : 0,
        isDefaultDelivery: address.isDefaultDelivery ? 1 : 0
      }));

      const addressPayload = {
        createdBy: this.authService.username(),
        addresses: formattedAddresses
      };

      await firstValueFrom(
        this.customerService.updateAddresses(customerId, addressPayload)
      );

      this.notification.set({
        type: 'success',
        message: 'Addresses updated successfully.',
      });

    } catch (error) {
      this.notification.set({
        type: 'error',
        message: 'Failed to update addresses.',
      });
      console.error(error);
    } finally {
      this.loading.set(false);
    }
  }

  // Separate method to update contact persons
  protected async updateContactPersons(): Promise<void> {
    const customerId = this.currentCustomerId();
    
    if (!customerId) {
      this.notification.set({
        type: 'alert',
        message: 'Customer ID not found. Cannot update contact persons.',
        timeout: 3000
      });
      return;
    }

    const customerData = { ...this.customer() };

    // Validate contact persons
    const hasValidContact = customerData.contactPersons.length > 0 && customerData.contactPersons.every(contact => {
      return contact.contactPersonName?.trim() && contact.title !== -1;
    });

    if (!hasValidContact) {
      this.notification.set({
        type: 'alert',
        message: 'Please fill all required contact person fields.',
        timeout: 3000
      });
      return;
    }

    // Email validation for contact persons
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    const invalidContactEmails = customerData.contactPersons.filter(
      contact => contact.contactPersonEmail && !emailRegex.test(contact.contactPersonEmail)
    );
    
    if (invalidContactEmails.length > 0) {
      this.notification.set({
        type: 'alert',
        message: 'Please enter valid email addresses for all Contact Persons.',
        timeout: 3000
      });
      return;
    }

    try {
      this.loading.set(true);

      const formattedContactPersons = customerData.contactPersons.map(contact => ({
        title: contact.title,
        contactPersonName: contact.contactPersonName,
        contactPersonDesignation: contact.contactPersonDesignation || '',
        contactPersonOfficePhoneNumber: contact.contactPersonOfficePhoneNumber || '',
        contactPersonMobileNumber: contact.contactPersonMobileNumber || '',
        contactPersonEmail: contact.contactPersonEmail || ''
      }));

      const contactPersonPayload = {
        customerId: customerId,
        createdBy: this.authService.username(),
        contactPersons: formattedContactPersons
      };

      await firstValueFrom(
        this.customerService.updateContactPersons(contactPersonPayload)
      );

      this.notification.set({
        type: 'success',
        message: 'Contact persons updated successfully.',
      });

    } catch (error) {
      this.notification.set({
        type: 'error',
        message: 'Failed to update contact persons.',
      });
      console.error(error);
    } finally {
      this.loading.set(false);
    }
  }

  private populateDataForUpdate(data: any): void {    
    this.originalCustomerCode.set(data.customerCode);
    this.customer.set({
      customerName: data.customerName || '',
      customerVatRegNo: data.customerVatRegNo || '',
      customerSvatRegNo: data.customerSvatRegNo || '',
      paymentTerm: data.paymentTerm,
      creditPeriod: data.noOfDays || undefined,
      currencyId: data.currency?.currencyId || -1,
      isTaxApply: data.isTaxApply === 1,
      invoiceTypeId: data.invoiceType?.invoiceTypeId || -1,
      telephoneNo: data.telephoneNo || '',
      faxNo: data.faxNo || '',
      emailAddress: data.email || '',
      isActive: data.isActive || 0,
      salesOwnerId: data.salesOwner[0]?.salesOwnerId || -1,
      wearhouseCode: data.wearhouseCode,
      isBOIRegistered: data.isBoiRegistered,
      addresses: this.mapAddressesFromAPI(data.addresses || []),
      contactPersons: this.mapContactPersonsFromAPI(data.contactPersons || []),
      invFormat: data.invFormat,
      plFormat: data.plFormat,
      boxFormat: data.boxFormat,
      pckFormat: data.pckFormat
    });
    
    // Store original customer data after populating
    this.storeOriginalCustomer();
  }

  private mapAddressesFromAPI(addresses: any[]): CustomerAddressDTO[] {
    return addresses.map(addr => ({
      addressId: addr.addressId,
      addressLineOne: addr.addressLineOne || '',
      addressLineTwo: addr.addressLineTwo || '',
      addressLineThree: addr.addressLineThree || '',
      country: addr.countryOb?.countryId || -1,
      show: addr.city || '',
      useForInvoice: addr.useForInvoice === 1,
      isDefaultInvoice: addr.isDefaultInvoice === 1,
      useForDelivery: addr.useForDelivery === 1,
      isDefaultDelivery: addr.isDefaultDelivery === 1,
      status:addr.status
    }));
  }

  private mapContactPersonsFromAPI(contactPersons: any[]): CustomerContactPersonDTO[] {
    return contactPersons.map(cp => ({
      contactPersonId: cp.contactPersonId,
      title: cp.titleId || -1,
      contactPersonName: cp.contactPersonName || '',
      contactPersonDesignation: cp.contactPersonDesignation || '',
      contactPersonOfficePhoneNumber: cp.contactPersonOfficePhoneNumber || '',
      contactPersonMobileNumber: cp.contactPersonMobileNumber || '',
      contactPersonEmail: cp.contactPersonEmail || '',
      status:cp.status
    }));
  }
 
  protected onDisabledButtonHover(): void {
    if (this.isValidatingCode()) {
      this.notification.set({
        type: 'alert',
        message: 'Please wait while the customer code is being validated.',
        timeout: 3000
      });
      return;
    }

    const errors = this.validationErrors();
    if (!errors.length) return;

    this.notification.set({
      type: 'alert',
      message: errors.length === 1
        ? errors[0].message
        : `${errors.length} fields still need attention: ${errors.map(e => e.label).join(', ')}.`,
      timeout: 3000
    });
  }

  protected cancel(): void {
    this.submitted.set(false);
    this.originalCustomerCode.set('');
    this.isValidatingCode.set(false);
    this.originalCustomer = null;
    this.customer.set({
      customerName: '',
      customerVatRegNo: '',
      customerSvatRegNo: '',
      paymentTerm: '',
      creditPeriod: undefined,
      currencyId: -1,
      isTaxApply: false,
      taxGroupId: null,
      invoiceTypeId: -1,
      telephoneNo: '',
      faxNo: '',
      emailAddress: '',
      invFormat: '',
      plFormat: '',
      boxFormat: '',
      pckFormat: '',
      isActive: 0,
      salesOwnerId: -1,
      wearhouseCode: '',
      isBOIRegistered: false,
      addresses: [],
      contactPersons: []
    });

    this.resetSearchTrigger.set(true);
    setTimeout(() => this.resetSearchTrigger.set(false), 0);
  }

  protected async toggleCustomerStatus(): Promise<void> {
    // Check if toggle is disabled
    if (this.isToggleDisabled()) {
      this.notification.set({
        type: 'alert',
        message: this.getToggleTooltipMessage(),
        timeout: 3000
      });
      return;
    }

    const activeCustomer = this.customerService.active();
    if (!activeCustomer) {
      this.notification.set({
        type: 'alert',
        message: 'No customer selected for status update.',
        timeout: 3000
      });
      return;
    }

    const newStatus = this.customer().isActive === 0 ? 1 : 0;
    const statusText = newStatus === 1 ? 'activate' : 'deactivate';

    try {
      this.loading.set(true);

      await firstValueFrom(
        this.customerService.toggleCustomerStatus(
          activeCustomer.customerId,
          newStatus,
          this.authService.username()
        )
      );

      this.customer.update(current => ({ ...current, isActive: newStatus }));
      
      // Reset original customer data after status toggle
      this.resetOriginalCustomer();

      this.customerService.notifyCustomerCreated();
      this.notification.set({
        type: 'success',
        message: `Customer ${statusText}d successfully.`,
      });

    } catch (error) {
      this.notification.set({
        type: 'error',
        message: `Failed to ${statusText} the customer.`,
      });
      console.error(error);
    } finally {
      this.loading.set(false);
    }
  }

  ngOnDestroy(): void {
    this.cancel();
  }
}